/** The soldier mesh: boxes rigidly skinned to the rig, coloured from the palette by side and kit. */

import type { ObjectiveKind } from '../../sim/types';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import type { Palette } from '../palette';
import { ghillieColors, ghillieWrap, wrappedPack } from './ghillie';
import { B, restWorld } from './rig';

/**
 * `captive`: POWs and hostages - grey fatigues, no webbing, no accent of either side. The expansion's
 * people (10 §2), none of whom carries the enemy's colour or a rifle:
 * `scientist` - white lab coat to the knee over dark trousers, bare-headed;
 * `pilot` - grey flight suit, OUR amber life vest, white flying helmet with a dark visor, and a
 *   field dressing on his left thigh (he is wounded: he walks, never runs);
 * `defector` - their uniform and officer's cap, but a plain long coat, a pale cap band where the red
 *   one was, and a white band on his left arm - the agreed sign.
 */
export type Side = 'ranger' | 'enemy' | 'officer' | 'captive' | 'scientist' | 'pilot' | 'defector';

export interface SoldierLook {
  side: Side;
  skin: 'skinA' | 'skinB' | 'skinC';
  /** `none` is a bare head (captives). `flightHelmet` is the pilot's. */
  headgear: 'helmet' | 'cap' | 'beret' | 'flightHelmet' | 'none';
  pack: boolean;
  /** False leaves the carbine off the weapon bone: captives, a captured officer. Default true. */
  rifle?: boolean;
  /**
   * Specialist weapons (02 §9.1), each replacing the rifle. The palette has no blue or brown
   * uniform to tell these men by, so their kit does it:
   * `flamethrower` - banded fuel tank on the back, lance in the hands, and a `paper` helmet band;
   * `rocket` - a launch tube across the right shoulder, warm warhead showing at the front.
   */
  kit?: 'flamethrower' | 'rocket';
  /**
   * What marks out the one man a mission is about (`targetLook`). Each REPLACES his webbing, so an
   * armed officer stays inside the 350 triangles:
   * `general` - greatcoat to the knee, red lapels, gold where an officer wears white and red;
   * `satchel` - the courier's despatch case on his left hip, its strap across his chest.
   */
  accessory?: 'general' | 'satchel';
  /**
   * ROUND7: the ghillie wrap (ghillie.ts) - biome-coloured scrim over the helmet, shoulders and pack, in
   * place of the webbing and the helmet's rim. Only the ranger wears one; absent is false.
   */
  ghillie?: boolean;
}

/**
 * The look of the man a `killTarget` / `lootTarget` mission is about. `Enemy` itself does not say he is
 * the one; the sim exposes him as `world.objective.targetEnemyId` (-1 until the first ground tick, or
 * until a dormant target arrives), so the caller builds - or rebuilds - his soldier once the id is known.
 */
export function targetLook(base: SoldierLook, objective: ObjectiveKind): SoldierLook {
  if (objective === 'killTarget') return { ...base, accessory: 'general' };
  if (objective === 'lootTarget') return { ...base, accessory: 'satchel' };
  return base;
}

/** Cloth ramps by side: [body, dark (trousers, headgear), light (shoulders)]. */
function dress(look: SoldierLook): readonly [string, string, string] {
  switch (look.side) {
    case 'ranger': return ['uniformUS.1', 'uniformUS.0', 'uniformUS.2'];
    // Captives wear ragged drab over grey: deliberately neither army's ramp.
    case 'captive': return ['canvas.1', 'metal.1', 'canvas.2'];
    case 'scientist': return ['paper.0', 'metal.0', 'paper.0'];
    case 'pilot': return ['metal.1', 'metal.0', 'metal.2'];
    default: return ['uniformEnemy.1', 'uniformEnemy.0', 'uniformEnemy.2'];
  }
}

export function buildGeometry(p: Palette, look: SoldierLook) {
  const mb = new MeshBuilder();
  const [body, dark, light] = dress(look);
  const cloth = p.color(body);
  const clothDark = p.color(dark);
  const clothLight = p.color(light);
  const skin = p.color(`${look.skin}.1`);
  const skinShade = p.color(`${look.skin}.0`);
  const boot = p.color('boot.0');
  const web = p.color('canvas.0');
  const accent = p.color(look.side === 'ranger' ? 'playerAccent.0' : 'enemyAccent.0');
  const general = look.accessory === 'general';
  const soldier = look.side === 'ranger' || look.side === 'enemy' || look.side === 'officer';
  /** The expansion's people never carry a rifle, whatever `rifle` says. */
  const civilian = look.side === 'scientist' || look.side === 'pilot' || look.side === 'defector';
  const longCoat = general || look.side === 'scientist' || look.side === 'defector';
  const scrim = look.ghillie === true ? ghillieColors(p) : null;
  const at = (bone: number, fn: () => void): void => {
    const w = restWorld(bone);
    mb.setBone(bone).with(place(w.x, w.y, w.z), fn);
  };
  /** A box hanging down from the bone origin, as limbs do. */
  const hang = (w: number, h: number, d: number, color: typeof cloth, dz = 0): void => {
    mb.box(w, h, d, color, place(0, -h, dz));
  };

  at(B.pelvis, () => {
    mb.box(0.42, 0.22, 0.27, clothDark, place(0, -0.08, 0));
    mb.box(0.46, 0.08, 0.3, web, place(0, 0.08, 0)); // belt
    if (longCoat) mb.box(0.48, 0.38, 0.32, cloth, place(0, -0.42, 0)); // coat skirt, to the knee
    if (look.accessory === 'satchel') mb.box(0.14, 0.26, 0.24, p.color('timber.1'), place(0.29, -0.2, 0.02));
  });
  at(B.spine, () => {
    mb.box(0.5, 0.5, 0.29, { all: cloth, top: clothLight });
    if (look.accessory === 'satchel') {
      mb.box(0.1, 0.62, 0.31, p.color('timber.1'), place(0, -0.02, 0, 0, 1, 0, 0.6)); // the strap, shoulder to hip
    } else if (general) {
      mb.box(0.3, 0.2, 0.31, accent, place(0, 0.26, 0)); // red lapels
    } else if (soldier && !scrim) {
      mb.box(0.12, 0.44, 0.31, web, place(0.14, 0.04, 0)); // webbing straps
      mb.box(0.12, 0.44, 0.31, web, place(-0.14, 0.04, 0));
    }
    const pack = scrim ? wrappedPack(scrim) : { all: web, top: p.color('canvas.1') };
    if (look.pack) mb.box(0.4, 0.46, 0.2, pack, place(0, 0.04, -0.24));
    if (look.kit === 'flamethrower') {
      // Fuel tank: the enemy's band round it is this man's accent, and his death warrant.
      // (The band is a box, not a ring: it keeps this man inside the 350-triangle budget.)
      mb.cylinder(0.15, 0.15, 0.52, 5, p.color('metal.1'), place(0, 0.02, -0.29), p.color('metal.2'));
      mb.box(0.32, 0.12, 0.32, accent, place(0, 0.24, -0.29));
    } else if (look.kit === 'rocket') {
      // Tube on the right shoulder, along the line of the chest so it turns with his aim.
      mb.cylinder(0.08, 0.08, 1.0, 6, web, place(-0.27, 0.58, -0.5, 0, 1, Math.PI / 2), p.color('ink.0'));
      mb.cylinder(0.075, 0, 0.2, 6, p.color('fire.1'), place(-0.27, 0.58, 0.5, 0, 1, Math.PI / 2));
    }
    if (look.side === 'ranger') {
      mb.box(0.36, 0.1, 0.36, accent, place(0, 0.46, 0)); // scarf: the player's colour (05 §4.3)
    } else if (look.side === 'officer' || look.side === 'defector') {
      const boards = p.color(general ? 'fire.2' : 'paper.0');
      mb.box(0.16, 0.06, 0.3, boards, place(0.2, 0.5, 0)); // shoulder boards
      mb.box(0.16, 0.06, 0.3, boards, place(-0.2, 0.5, 0));
    } else if (look.side === 'pilot') {
      mb.box(0.4, 0.24, 0.33, p.color('playerAccent.0'), place(0, 0.22, 0)); // life vest: he is one of ours
    }
  });
  // Whatever he carries is modelled round its grip with the business end along +z; poses.ts
  // places the bone. Nothing bound to it is fine: an unarmed man simply has an idle bone.
  at(B.weapon, () => {
    if (look.kit === 'flamethrower') {
      mb.box(0.07, 0.09, 0.85, p.color('metal.0'), place(0, 0, 0.1));
      mb.box(0.1, 0.1, 0.1, p.color('fire.1'), place(0, 0, 0.56)); // pilot flame
    } else if (look.kit === undefined && look.rifle !== false && !civilian) {
      mb.box(0.08, 0.12, 0.8, p.color('metal.0'), place(0, 0, 0.12));
      mb.box(0.06, 0.2, 0.09, p.color('metal.0'), place(0, -0.17, 0.12));
      mb.box(0.09, 0.14, 0.26, p.color('timber.0'), place(0, -0.02, -0.36));
    }
  });
  at(B.head, () => {
    mb.box(0.12, 0.08, 0.12, skinShade, place(0, -0.04, 0)); // neck
    mb.box(0.33, 0.31, 0.32, { all: skin, back: skinShade, top: skinShade }, place(0, 0.02, 0));
    mb.box(0.07, 0.06, 0.04, p.color('ink.0'), place(0.08, 0.18, 0.15)); // eyes
    mb.box(0.07, 0.06, 0.04, p.color('ink.0'), place(-0.08, 0.18, 0.15));
    if (look.headgear === 'helmet') {
      mb.box(0.42, 0.17, 0.42, { all: clothDark, top: scrim?.top ?? cloth }, place(0, 0.26, 0));
      if (!scrim) mb.box(0.46, 0.06, 0.46, clothDark, place(0, 0.23, 0)); // the rim, under the scrim if he wears it
      const band = look.kit === 'flamethrower' ? p.color('paper.0') : accent;
      if (look.side !== 'ranger') mb.box(0.44, 0.06, 0.44, band, place(0, 0.29, 0));
    } else if (look.headgear === 'cap') {
      mb.box(0.4, 0.13, 0.4, { all: clothDark, top: cloth }, place(0, 0.3, 0));
      mb.box(0.36, 0.04, 0.18, p.color('ink.0'), place(0, 0.29, 0.26)); // peak
      const band = general ? p.color('fire.2') : look.side === 'defector' ? p.color('paper.0') : accent;
      mb.box(0.42, 0.05, 0.42, band, place(0, 0.3, 0));
    } else if (look.headgear === 'beret') {
      mb.box(0.4, 0.1, 0.4, accent, place(0.03, 0.3, 0));
    } else if (look.headgear === 'flightHelmet') {
      mb.box(0.44, 0.24, 0.44, p.color('paper.0'), place(0, 0.2, -0.01));
      mb.box(0.36, 0.09, 0.06, p.color('ink.1'), place(0, 0.26, 0.2)); // visor, pushed up clear of his eyes
    } else {
      mb.box(0.35, 0.08, 0.34, p.color('boot.1'), place(0, 0.3, -0.01)); // cropped hair
    }
  });
  for (const [upper, fore] of [
    [B.lUpperArm, B.lForeArm],
    [B.rUpperArm, B.rForeArm],
  ] as const) {
    at(upper, () => {
      hang(0.15, 0.32, 0.15, cloth);
      const banded = look.side === 'defector' && upper === B.lUpperArm;
      if (banded) mb.box(0.18, 0.1, 0.18, p.color('paper.0'), place(0, -0.2, 0)); // the agreed sign
    });
    at(fore, () => {
      hang(0.13, 0.26, 0.13, cloth);
      mb.box(0.12, 0.1, 0.12, skin, place(0, -0.36, 0));
    });
  }
  for (const [thigh, shin] of [
    [B.lThigh, B.lShin],
    [B.rThigh, B.rShin],
  ] as const) {
    at(thigh, () => {
      hang(0.19, 0.44, 0.2, clothDark);
      if (look.side === 'pilot' && thigh === B.lThigh) {
        mb.box(0.22, 0.14, 0.23, p.color('paper.0'), place(0, -0.3, 0)); // field dressing
        mb.box(0.08, 0.08, 0.04, p.color('blood.0'), place(0.02, -0.27, 0.11));
      }
    });
    at(shin, () => {
      hang(0.16, 0.34, 0.17, clothDark);
      mb.box(0.18, 0.13, 0.3, boot, place(0, -0.46, 0.05));
    });
  }
  if (scrim) ghillieWrap(mb, p, at);
  return mb.build();
}
